import { API_BASE } from '@/utils/env'
import { authState, ensureAuth, forgetAll, invalidateAccessToken, renewSession } from '@/utils/token'

/** 统一响应：{ code, msg, data }，code === 0 为成功。 */
interface ApiEnvelope<T> {
  code: number
  msg: string
  data: T
}

type FetchResult = { status: number; body: ApiEnvelope<unknown> | null }

async function fetchWithToken(path: string, token: string): Promise<FetchResult> {
  let res: Response
  try {
    res = await fetch(`${API_BASE}${path}`, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
  } catch (err) {
    throw new Error(`无法连接聚合接口：${(err as Error).message}`)
  }

  let body: ApiEnvelope<unknown> | null = null
  try {
    body = (await res.json()) as ApiEnvelope<unknown>
  } catch (err) {
    throw new Error(`接口返回非 JSON（HTTP ${res.status}）`)
  }
  return { status: res.status, body }
}

export async function apiGet<T>(path: string): Promise<T> {
  // 请求前先保证凭据可用（临近到期会先用 refreshToken / 账号密码自动续期）
  const token = await ensureAuth()

  let result = await fetchWithToken(path, token)

  // accessToken 在服务端已失效：丢弃它 → 强制续期/重新登录一次 → 原请求重试一次
  if (result.status === 401) {
    invalidateAccessToken()
    if (await renewSession()) {
      result = await fetchWithToken(path, authState.token)
    }
    if (result.status === 401) {
      // 续期与重新登录都没救回来：清空凭据 + 重弹输入层（现有 401 语义）
      forgetAll('登录状态已失效且无法自动续期，请重新输入凭据')
      throw new Error(`HTTP 401${result.body && result.body.msg ? `：${result.body.msg}` : ''}`)
    }
  }

  if (result.status < 200 || result.status >= 300) {
    throw new Error(`HTTP ${result.status}${result.body && result.body.msg ? `：${result.body.msg}` : ''}`)
  }
  if (!result.body || result.body.code !== 0) {
    throw new Error(result.body?.msg || '接口返回业务错误')
  }
  return result.body.data as T
}
